import { NavItem } from '@/app/types/navigation';
import { NavDropdownLabelButton } from '@/modules/LandingHeaderV2/components/NavDropdownLabelButton/NavDropdownLabelButton';
import { NavDropdownSubItem } from '@/modules/LandingHeaderV2/components/NavDropdownSubItem/NavDropdownSubItem';
import cx from 'classnames';
import { useRouter } from 'next/router';
import { useMemo, useState } from 'react';

import { Button } from '@material-ui/core';
import ChevronLeftIcon from '@material-ui/icons/ChevronLeft';

import styles from './NavDropdown.module.scss';

export type NavDropdownProps = NavItem & {
  onNavItemClick?: () => void;
  isSmallScreen: boolean;
};

export const NavDropdown = ({
  onNavItemClick,
  label,
  subItems,
  testid,
  to,
  id,
  trackEventName,
  isLoggedInDropdown,
  userInitials,
  isSmallScreen,
}: NavDropdownProps) => {
  const [open, setOpen] = useState(false);
  const router = useRouter();

  const hasCurrentInSubMenu = useMemo<boolean>(() => {
    const currentPath = router?.asPath;
    if (!currentPath || !subItems) {
      return false;
    }
    return subItems.some(item => item.to === currentPath);
  }, [router?.asPath, subItems]);
  const isOrHasActiveLink = router?.asPath === to || hasCurrentInSubMenu;

  const handleDropdownButtonClick = () => {
    if (isSmallScreen) {
      setOpen(true);
    }
  };

  const handleOnMouseOver = () => {
    if (isSmallScreen) {
      return;
    }
    setOpen(true);
  };

  const handleOnMouseOut = () => {
    if (isSmallScreen) {
      return;
    }
    setOpen(false);
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.code === 'Escape') {
      setOpen(false);
    }

    if (event.code === 'Enter' || event.code === 'Space') {
      setOpen(prevOpen => !prevOpen);
    }
  };

  return (
    <div
      className={styles.navDropdownContainer}
      onMouseOut={handleOnMouseOut}
      onMouseOver={handleOnMouseOver}
      onFocus={handleOnMouseOver}
      onBlur={handleOnMouseOut}
      onKeyDown={handleKeyDown}
      data-testid={testid}
      role="button"
      tabIndex={0}
    >
      <NavDropdownLabelButton
        isSmallScreen={isSmallScreen}
        handleOnClick={handleDropdownButtonClick}
        to={to}
        label={label}
        testid={testid}
        isLoggedInDropdown={isLoggedInDropdown}
        userInitials={userInitials}
        trackEventName={trackEventName}
        isOrHasActiveLink={isOrHasActiveLink}
        isDropdownOpen={open}
        onNavItemClick={onNavItemClick}
      />
      <div
        className={cx(
          styles.navDropdownSubItemContainer,
          isLoggedInDropdown && styles.navDropdownSubItemContainerLoggedIn,
          open ? styles.subMenuItemContainerOpen : styles.subMenuItemContainerClosed,
        )}
      >
        <div
          className={cx(styles.navDropdownSubItemInner, open && styles.navDropdownSubItemInnerOpen)}
        >
          {isSmallScreen && open && (
            <>
              <Button
                className={cx(styles.navDropdownSubItem, styles.navDropdownGoBackButton)}
                onClick={() => setOpen(false)}
                data-testid="nav-dropdown-close-button"
              >
                <div className={styles.navDropdownGoBackText}>
                  <ChevronLeftIcon />
                  Go Back
                </div>
              </Button>
              {!isLoggedInDropdown && (
                <NavDropdownSubItem
                  to={to}
                  testid={`${testid}-mobile-root-link`}
                  id={id}
                  label={`${label} Page`}
                  trackEventName={trackEventName}
                  onNavItemClick={onNavItemClick}
                  setOpen={setOpen}
                  isActiveLink={router?.asPath === to}
                />
              )}
            </>
          )}
          {subItems?.map(item => (
            <NavDropdownSubItem
              key={`${item.label}`}
              id={item.id}
              label={item.label}
              to={item.to}
              testid={item.testid}
              trackEventName={item.trackEventName}
              isActiveLink={router?.asPath === item.to}
              onNavItemClick={onNavItemClick}
              setOpen={setOpen}
            />
          ))}
        </div>
      </div>
    </div>
  );
};
